import { useState, useEffect } from 'react';

const statusColors: Record<string, string> = {
  open: 'bg-red-50 text-red-600',
  'in-progress': 'bg-amber-50 text-amber-600',
  resolved: 'bg-emerald-50 text-emerald-700',
};

export default function InquiriesSection() {
  const [inquiries, setInquiries] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [selected, setSelected] = useState<any | null>(null);
  const [reply, setReply] = useState('');
  const [replySent, setReplySent] = useState(false);
  const [submittingReply, setSubmittingReply] = useState(false);
  const [replyError, setReplyError] = useState<string | null>(null);

  const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:5001';

  const fetchInquiries = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE}/api/inquiries`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (res.ok) {
        const data = await res.json();
        setInquiries(data);
      }
    } catch (error) {
      console.error('Failed to fetch enquiries:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInquiries();
  }, []);

  const filtered = filter === 'all' ? inquiries : inquiries.filter((i: any) => i.status === filter);

  const updateStatus = async (id: string, status: string) => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE}/api/inquiries/${id}/status`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ status })
      });

      if (res.ok) {
        setInquiries((prev) => prev.map((i) => i._id === id ? { ...i, status } : i));
        if (selected?._id === id) setSelected((prev: any) => prev ? { ...prev, status } : null);
      }
    } catch (error) {
      console.error('Failed to update status:', error);
    }
  };

  const handleReply = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!reply.trim() || !selected) return;

    setSubmittingReply(true);
    setReplyError(null);

    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE}/api/inquiries/${selected._id}/reply`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ message: reply })
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Failed to send reply');

      setReplySent(true);
      setReply('');
      // Update local state to show 'resolved' status
      setInquiries((prev) => prev.map((i) => i._id === selected._id ? { ...i, status: 'resolved' } : i));
      setSelected((prev: any) => prev ? { ...prev, status: 'resolved' } : null);

      setTimeout(() => setReplySent(false), 3000);
    } catch (error: any) {
      console.error('Failed to send reply:', error);
      setReplyError(error.message || 'Failed to send reply');
    } finally {
      setSubmittingReply(false);
    }
  };

  if (loading) {
    return <div className="text-center py-12 text-slate-400">Loading Enquiries...</div>;
  }

  return (
    <div className="flex flex-col gap-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-xl font-bold text-slate-800" style={{ fontFamily: "'Inter', sans-serif" }}>Enquiries</h2>
          <p className="text-slate-500 text-sm">{inquiries.filter((i) => i.status === 'open').length} open enquiries require attention</p>
        </div>
        <div className="flex items-center bg-slate-100 rounded-full p-1 gap-1">
          {['all', 'open', 'in-progress', 'resolved'].map((f) => (
            <button key={f} onClick={() => setFilter(f)} className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-all cursor-pointer whitespace-nowrap capitalize ${filter === f ? 'bg-white text-slate-800' : 'text-slate-500 hover:text-slate-700'}`}>
              {f === 'all' ? 'All' : f}
            </button>
          ))}
        </div>
      </div>

      <div className="flex gap-5">
        {/* List */}
        <div className="flex-1 flex flex-col gap-2 min-w-0">
          {filtered.map((inq) => (
            <div
              key={inq._id}
              onClick={() => setSelected(inq)}
              className={`bg-white rounded-xl border p-4 cursor-pointer transition-all ${selected?._id === inq._id ? 'border-emerald-400' : 'border-slate-100 hover:border-slate-200'}`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-9 h-9 flex items-center justify-center bg-slate-100 rounded-full flex-shrink-0 text-xs font-bold text-slate-600">
                    {inq.name.split(' ').map((n: string) => n[0]).join('')}
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-slate-800">{inq.name}</p>
                    <p className="text-xs text-slate-500 truncate">{inq.subject}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${statusColors[inq.status]}`}>{inq.status}</span>
                </div>
              </div>
              <p className="text-xs text-slate-400 mt-2 line-clamp-1 pl-12">{inq.message}</p>
            </div>
          ))}
          {filtered.length === 0 && (
            <div className="text-center py-12 text-slate-400 text-sm">No enquiries found</div>
          )}
        </div>

        {/* Detail Panel */}
        {selected && (
          <div className="w-80 flex-shrink-0 bg-white rounded-xl border border-slate-100 p-5 flex flex-col gap-4 self-start sticky top-4">
            <div className="flex items-center justify-between">
              <h3 className="font-bold text-slate-800 text-sm">Enquiry Detail</h3>
              <button onClick={() => setSelected(null)} className="w-6 h-6 flex items-center justify-center text-slate-400 hover:text-slate-600 cursor-pointer">
                <i className="ri-close-line"></i>
              </button>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 flex items-center justify-center bg-slate-100 rounded-full text-sm font-bold text-slate-600">
                {selected.name.split(' ').map((n: string) => n[0]).join('')}
              </div>
              <div>
                <p className="font-semibold text-slate-800 text-sm">{selected.name}</p>
                <p className="text-xs text-slate-500">{selected.email}</p>
                {selected.contactNo && (
                  <p className="text-xs text-slate-500">{selected.contactNo}</p>
                )}
              </div>
            </div>
            <div>
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Subject</p>
              <p className="text-sm text-slate-700">{selected.subject}</p>
            </div>
            <div>
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Message</p>
              <p className="text-sm text-slate-600 leading-relaxed">{selected.message}</p>
            </div>
            <div className="flex gap-2">
              <span className={`text-xs font-semibold px-2 py-1 rounded-full ${statusColors[selected.status]}`}>{selected.status}</span>
            </div>
            <div>
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Update Status</p>
              <div className="flex gap-1.5 flex-wrap">
                {['open', 'in-progress', 'resolved'].map((s) => (
                  <button key={s} onClick={() => updateStatus(selected._id, s)} className={`text-xs font-semibold px-3 py-1.5 rounded-lg border transition-colors cursor-pointer whitespace-nowrap capitalize ${selected.status === s ? 'bg-emerald-700 text-white border-transparent' : 'bg-white text-slate-600 border-slate-200 hover:border-emerald-400'}`}>
                    {s}
                  </button>
                ))}
              </div>
            </div>
            <form onSubmit={handleReply} className="flex flex-col gap-2">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Reply</p>
              <textarea
                value={reply}
                onChange={(e) => setReply(e.target.value)}
                placeholder="Type your reply..."
                rows={3}
                maxLength={500}
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-emerald-500 resize-none"
              />
              {replySent && <p className="text-xs text-emerald-600 font-semibold">Reply sent and email delivered!</p>}
              {replyError && <p className="text-xs text-red-500 font-semibold">{replyError}</p>}
              <button
                type="submit"
                disabled={submittingReply}
                className={`bg-emerald-700 hover:bg-emerald-800 text-white text-xs font-semibold py-2 rounded-lg transition-colors cursor-pointer whitespace-nowrap ${submittingReply ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                {submittingReply ? 'Sending...' : 'Send Reply'}
              </button>
            </form>
          </div>
        )}
      </div>
    </div>
  );
}
